import React from "react";
import { Field, ErrorMessage } from "formik";
import { Row, Col, Button, FormGroup, Card, CardBody } from "reactstrap";
import { Plus, Trash2, Save, Edit } from "react-feather";
import Select from "react-select";
import { UncontrolledTooltip } from "reactstrap";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { H5 } from "../../../AbstractElements";
import { SlabCardProps } from "../../../types/tax-types/TaxTypes";
import { useTranslation } from "react-i18next";

const SlabCard: React.FC<SlabCardProps> = (props) => {
  const {
    slab,
    slabIdx,
    slabErrors,
    slabRanges,
    replace,
    remove,
    push,
    setFieldTouched,
    generateId,
    customReactSelectStyles,
  } = props;

  const { t } = useTranslation();

  return (
    <div key={slab.id}>
      {slab.isEditing ? (
        <>
          <Row className="mt-2">
            <Col md={6}>
              <FormGroup>
                <label>
                  {t("SlabName")}

                  <span className="txt-danger">*</span>
                </label>
                <Field
                  name={`slab_rule[${slabIdx}].slab_name`}
                  type="text"
                  className="form-control"
                  placeholder="Slab Name"
                />
                <div>
                  <ErrorMessage
                    name={`slab_rule[${slabIdx}].slab_name`}
                    component="span"
                    className="text-danger mt-1 d-block small"
                  />
                </div>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <label>{t("SlabDescription")}</label>
                <Field
                  name={`slab_rule[${slabIdx}].slab_description`}
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Slab Description"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row className="g-2 mb-1">
            <Col md={2}></Col>
            <Col md={2}></Col>
            <Col md={2}></Col>
            <Col md={2}></Col>
            <Col md={2}></Col>
            <Col md={1}></Col>
            <Col
              md={1}
              className="d-flex justify-content-end align-items-center p-0"
            >
              <div className="d-flex align-items-center">
                <Button
                  className="p-2"
                  id={`add-range-${slabIdx}`}
                  color="link"
                  size="sm"
                  type="button"
                  onClick={() => {
                    const updatedRanges = [
                      ...slabRanges,
                      {
                        id: generateId(),
                        range_order: (slabRanges.length + 1).toString(),
                        range_income_from: "",
                        range_income_to: "",
                        range_calculation_mode: "fixed",
                        range_fixed_amount: null,
                        range_percentage: null,
                        isEditing: true,
                      },
                    ];
                    replace(slabIdx, {
                      ...slab,
                      slab_range: updatedRanges,
                    });
                  }}
                >
                  <Plus size={18} />
                </Button>
                <UncontrolledTooltip target={`add-range-${slabIdx}`}>
                  {t("AddRange")}
                </UncontrolledTooltip>
                <Button
                  className="p-2"
                  id={`delete-slab-${slabIdx}`}
                  color="link"
                  size="sm"
                  type="button"
                  onClick={() => remove(slabIdx)}
                >
                  <Trash2 size={18} />
                </Button>
                <UncontrolledTooltip target={`delete-slab-${slabIdx}`}>
                  {t("DeleteSlab")}
                </UncontrolledTooltip>
                <Button
                  className="p-2"
                  id={`save-slab-${slabIdx}`}
                  color="link"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setFieldTouched(
                      `slab_rule[${slabIdx}].slab_name`,
                      true,
                      true
                    );
                    slabRanges.forEach((range: any, rangeIdx: number) => {
                      setFieldTouched(
                        `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_income_from`,
                        true,
                        true
                      );
                      setFieldTouched(
                        `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_income_to`,
                        true,
                        true
                      );
                      setFieldTouched(
                        `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_calculation_mode`,
                        true,
                        true
                      );
                      setFieldTouched(
                        range.range_calculation_mode === "fixed"
                          ? `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_fixed_amount`
                          : `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_percentage`,
                        true,
                        true
                      );
                    });
                    const allRangesValid = slabRanges.every(
                      (range: any, rangeIdx: number) =>
                        range.range_income_from &&
                        range.range_income_to &&
                        range.range_calculation_mode &&
                        (range.range_calculation_mode === "fixed"
                          ? range.range_fixed_amount !== null
                          : range.range_percentage !== null) &&
                        !slabErrors.slab_range?.[rangeIdx]?.range_income_from &&
                        !slabErrors.slab_range?.[rangeIdx]?.range_income_to &&
                        !slabErrors.slab_range?.[rangeIdx]
                          ?.range_calculation_mode &&
                        !slabErrors.slab_range?.[rangeIdx]?.[
                          range.range_calculation_mode === "fixed"
                            ? "range_fixed_amount"
                            : "range_percentage"
                        ]
                    );
                    if (
                      slab.slab_name &&
                      !slabErrors.slab_name &&
                      allRangesValid
                    ) {
                      replace(slabIdx, {
                        ...slab,
                        isEditing: false,
                      });
                    }
                  }}
                >
                  <Save size={18} />
                </Button>
                <UncontrolledTooltip target={`save-slab-${slabIdx}`}>
                  {t("SaveSlab")}
                </UncontrolledTooltip>
              </div>
            </Col>
          </Row>
          <DragDropContext
            onDragEnd={(result) => {
              if (!result.destination) return;
              const items = Array.from(slabRanges);
              const [reorderedItem] = items.splice(result.source.index, 1);
              items.splice(result.destination.index, 0, reorderedItem);
              const updatedItems = (items as Record<string, any>[]).map(
                (item, index) => ({
                  ...item,
                  range_order: (index + 1).toString(),
                })
              );
              replace(slabIdx, {
                ...slab,
                slab_range: updatedItems,
              });
            }}
          >
            <Droppable
              droppableId={`slab-${slabIdx}`}
              renderClone={(provided, snapshot, rubric) => {
                const futureOrder = rubric.source.index + 1;
                return (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className="range-card bg-light border border-1 border-gray rounded-3 d-flex align-items-center p-3 mb-2"
                  >
                    <span
                      className="badge bg-primary me-3"
                      style={{ minWidth: 26, fontSize: 12 }}
                    >
                      {futureOrder}
                    </span>
                    <span
                      className="drag-handle d-flex align-items-center justify-content-center me-3"
                      style={{
                        cursor: "grab",
                        fontSize: 22,
                        color: "#90caf9",
                      }}
                    >
                      ⠿
                    </span>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        gap: 18,
                      }}
                    ></div>
                  </div>
                );
              }}
            >
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {slabRanges.map((range: any, rangeIdx: number) => (
                    <Draggable
                      key={range.id}
                      draggableId={range.id}
                      index={rangeIdx}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="range-card bg-light border border-1 border-gray rounded-3 d-flex align-items-center p-3 mb-2"
                        >
                          <span
                            className="badge bg-primary me-3"
                            style={{ minWidth: 26, fontSize: 12 }}
                          >
                            {rangeIdx + 1}
                          </span>
                          <span
                            className="drag-handle d-flex align-items-center justify-content-center"
                            id={`drag-range-${slabIdx}-${rangeIdx}`}
                            style={{
                              cursor: "grab",
                              fontSize: 22,
                              color: "gray",
                            }}
                          >
                            ⠿
                          </span>
                          <div style={{ flex: 1 }}>
                            <Row className="g-2 align-items-start">
                              <Col xs="6" md="3">
                                <Field
                                  name={`slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_income_from`}
                                  type="number"
                                  min={0}
                                  className="form-control"
                                  placeholder={t("RangeFrom")}
                                />
                                <ErrorMessage
                                  name={`slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_income_from`}
                                  component="span"
                                  className="text-danger mt-1 d-block small"
                                />
                              </Col>
                              <Col xs="6" md="3">
                                <Field
                                  name={`slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_income_to`}
                                  type="number"
                                  min={0}
                                  className="form-control"
                                  placeholder={t("RangeTo")}
                                />
                                <ErrorMessage
                                  name={`slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_income_to`}
                                  component="span"
                                  className="text-danger mt-1 d-block small"
                                />
                              </Col>

                              <Col xs="7" md="3">
                                <Field
                                  name={`slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_calculation_mode`}
                                >
                                  {({ field, form }: any) => (
                                    <Select
                                      name={field.name}
                                      value={[
                                        { value: "fixed", label: "Fixed" },
                                        {
                                          value: "percentage",
                                          label: "Percentage",
                                        },
                                      ].find(
                                        (opt) => opt.value === field.value
                                      )}
                                      options={[
                                        { value: "fixed", label: "Fixed" },
                                        {
                                          value: "percentage",
                                          label: "Percentage",
                                        },
                                      ]}
                                      onChange={(option) => {
                                        form.setFieldValue(
                                          field.name,
                                          option ? option.value : null
                                        );
                                        form.setFieldValue(
                                          `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_fixed_amount`,
                                          null
                                        );
                                        form.setFieldValue(
                                          `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_percentage`,
                                          null
                                        );
                                      }}
                                      classNamePrefix="select"
                                      placeholder="Mode"
                                      styles={customReactSelectStyles}
                                    />
                                  )}
                                </Field>
                                <ErrorMessage
                                  name={`slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_calculation_mode`}
                                  component="span"
                                  className="text-danger mt-1 d-block small"
                                />
                              </Col>
                              <Col xs="5" md="2">
                                <Field
                                  name={
                                    range.range_calculation_mode === "fixed"
                                      ? `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_fixed_amount`
                                      : `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_percentage`
                                  }
                                  type="number"
                                  min={0}
                                  className="form-control"
                                  placeholder={
                                    range.range_calculation_mode === "fixed"
                                      ? "Amount"
                                      : "Rate"
                                  }
                                />
                                <ErrorMessage
                                  name={
                                    range.range_calculation_mode === "fixed"
                                      ? `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_fixed_amount`
                                      : `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_percentage`
                                  }
                                  component="span"
                                  className="text-danger mt-1 d-block small"
                                />
                              </Col>

                              <Col
                                xs="12"
                                md="1"
                                className="d-flex align-items-center justify-content-end justify-content-md-center mt-3 mt-md-3"
                              >
                                <Button
                                  id={`delete-range-${slabIdx}-${rangeIdx}`}
                                  color="link"
                                  size="sm"
                                  type="button"
                                  onClick={() => {
                                    const updatedRanges = slabRanges
                                      .filter(
                                        (_: any, idx: number) =>
                                          idx !== rangeIdx
                                      )
                                      .map((item: any, idx: number) => ({
                                        ...item,
                                        range_order: (idx + 1).toString(),
                                      }));
                                    replace(slabIdx, {
                                      ...slab,
                                      slab_range: updatedRanges,
                                    });
                                  }}
                                >
                                  <Trash2 size={18} />
                                </Button>
                                <UncontrolledTooltip
                                  target={`delete-range-${slabIdx}-${rangeIdx}`}
                                >
                                  {t("DeleteRange")}
                                </UncontrolledTooltip>
                              </Col>
                            </Row>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <div className="d-flex justify-content-end mt-3 mb-3">
            <Button
              color="info"
              onClick={() => {
                if (
                  slab.slab_name &&
                  !slabErrors.slab_name &&
                  slabRanges.every(
                    (range: any, rangeIdx: number) =>
                      range.range_income_from &&
                      range.range_income_to &&
                      range.range_calculation_mode &&
                      (range.range_calculation_mode === "fixed"
                        ? range.range_fixed_amount !== null
                        : range.range_percentage !== null) &&
                      !slabErrors.slab_range?.[rangeIdx]?.range_income_from &&
                      !slabErrors.slab_range?.[rangeIdx]?.range_income_to &&
                      !slabErrors.slab_range?.[rangeIdx]
                        ?.range_calculation_mode &&
                      !slabErrors.slab_range?.[rangeIdx]?.[
                        range.range_calculation_mode === "fixed"
                          ? "range_fixed_amount"
                          : "range_percentage"
                      ]
                  )
                ) {
                  push({
                    id: generateId(),
                    slab_name: "",
                    slab_description: "",
                    slab_range: [
                      {
                        id: generateId(),
                        range_order: "1",
                        range_income_from: "",
                        range_income_to: "",
                        range_calculation_mode: "fixed",
                        range_fixed_amount: null,
                        range_percentage: null,
                        isEditing: true,
                      },
                    ],
                    isEditing: true,
                  });
                } else {
                  setFieldTouched(
                    `slab_rule[${slabIdx}].slab_name`,
                    true,
                    true
                  );
                  slabRanges.forEach((_: any, rangeIdx: number) => {
                    setFieldTouched(
                      `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_income_from`,
                      true,
                      true
                    );
                    setFieldTouched(
                      `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_income_to`,
                      true,
                      true
                    );
                    setFieldTouched(
                      `slab_rule[${slabIdx}].slab_range[${rangeIdx}].range_calculation_mode`,
                      true,
                      true
                    );
                    setFieldTouched(
                      `slab_rule[${slabIdx}].slab_range[${rangeIdx}].${
                        slabRanges[rangeIdx].range_calculation_mode === "fixed"
                          ? "range_fixed_amount"
                          : "range_percentage"
                      }`,
                      true,
                      true
                    );
                  });
                }
              }}
            >
              <Plus size={16} className="me-2" />
              {t("AddSlab")}
            </Button>
          </div>
        </>
      ) : (
        <Card className="bg-light border border-1 border-gray rounded-3 mb-4 mt-2">
          <CardBody>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <H5>{slab.slab_name}</H5>
                {(slab.slab_range ?? []).map((range: any, idx: number) => (
                  <div key={range.id} className="small text-black mt-2">
                    {range.range_income_from} – {range.range_income_to},{" "}
                    {range.range_calculation_mode === "fixed"
                      ? `Fixed: ${range.range_fixed_amount}`
                      : `Percentage: ${range.range_percentage}%`}
                  </div>
                ))}
              </div>
              <div className="d-flex align-items-end gap-2 ms-auto">
                <Button
                  className="p-0"
                  id={`edit-slab-${slabIdx}`}
                  color="link"
                  size="sm"
                  type="button"
                  onClick={() => {
                    replace(slabIdx, {
                      ...slab,
                      isEditing: true,
                    });
                  }}
                >
                  <Edit size={18} />
                </Button>
                <UncontrolledTooltip target={`edit-slab-${slabIdx}`}>
                  {t("EditSlab")}
                </UncontrolledTooltip>
                <Button
                  className="p-0"
                  id={`delete-slab-${slabIdx}`}
                  color="link"
                  size="sm"
                  type="button"
                  onClick={() => remove(slabIdx)}
                >
                  <Trash2 size={18} />
                </Button>
                <UncontrolledTooltip target={`delete-slab-${slabIdx}`}>
                  {t("DeleteSlab")}
                </UncontrolledTooltip>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default React.memo(SlabCard);
